/**
 * The GST "Time Period" range tokens. CUSTOM is FE-only — it means "use the explicit dates" and is
 * never sent as a rangeKey; the other three are resolved server-side.
 */
export const GST_RANGE_KEYS = {
    ALL: 'ALL',
    LAST_FY: 'LAST_FY',
    LAST_12M: 'LAST_12M',
    CUSTOM: 'CUSTOM',
} as const;

export type GstRangeKey = (typeof GST_RANGE_KEYS)[keyof typeof GST_RANGE_KEYS];

/**
 * Applied GST "Time Period" range, in the shape every range-aware GST backend call takes:
 * either a server-resolved `rangeKey` or an explicit month-snapped date pair.
 * Built by `buildGstRangeParams` in revenueVendorExpensesApiHelpers.
 */
export interface GstRangeParams {
    rangeKey?: GstRangeKey;
    startDate?: string;
    endDate?: string;
}

/** Metadata returned by the GST analysis report generate API. */
export interface GstAnalysisReportMeta {
    mnemosyneDocId?: number | string;
    fileName?: string;
}

/** A generated report resolved to a viewable doc (id + display name). */
export interface GstAnalysisReportDoc {
    docId: number | string;
    name: string;
}

export type GstSummaryView = 'REVENUE' | 'EXPENSE';

/** Request for POST /unifier/getGstReturnSummary (requestId is added by the service). */
export interface GstReturnSummaryRequest extends GstRangeParams {
    orgId: string;
    view: GstSummaryView;
    /** Return the table is showing (GST_RETURN_CODES); the backend narrows the rows to it. */
    returnType: string;
}

export interface GstReturnSummaryHeader {
    value: string;
    type: string;
}

/** One month (or the total) of a summary row: return code → amount; null/missing = not available. */
export type GstReturnSummaryAmounts = Record<string, Record<string, number | null | undefined> | null | undefined>;

export interface GstReturnSummaryRow {
    key?: string;
    label: string;
    rowType?: string;
    amounts?: GstReturnSummaryAmounts;
    note?: string | null;
    notes?: string | Record<string, string> | null;
}

export interface GstReturnSummaryAvailableEntry {
    date: string;
    gst: string;
    state?: string | null;
    dataSource?: string | null;
}

export interface GstReturnSummary {
    view?: GstSummaryView;
    returns?: string[];
    headers?: GstReturnSummaryHeader[];
    rows?: GstReturnSummaryRow[];
    gstAvailable?: Record<string, GstReturnSummaryAvailableEntry[] | Record<string, { gsts?: GstReturnSummaryAvailableEntry[] }>>;
    appliedWindow?: Record<string, unknown>;
}

export interface GstListItem {
    gstNumber: string;
    gstStatus?: string;
    connected?: boolean;
    state?: string | null;
}

export interface GstConnectionStatus {
    totalCount?: number;
    totalGstList?: GstListItem[];
    gstMonthErrors?: Record<string, Record<string, { errorCd?: string; message?: string }>>;
}

export interface GstReturnSummaryResponseBody {
    gstReturnSummary?: GstReturnSummary | null;
    gstConnectionStatus?: GstConnectionStatus | null;
}
